/**
 * Polish UI strings for the Contact Request epic.
 *
 * ## Organisation
 * Keys are grouped by the feature or screen they belong to:
 *
 * | Prefix            | Used in                        |
 * | ----------------- | ------------------------------ |
 * | `VIEW_CR_*`       | `ViewContactRequest` page      |
 * | `UPDATE_STATUS_*` | `UpdateStatusModal` component  |
 *
 * ## Usage
 * Import the re-exported `CONTACT_REQUEST_LABELS` from `../constants/labels`
 * instead of importing this file directly.
 */
const pl = {
  // ─── ViewContactRequest page ──────────────────────────────────────────────

  /** Page title shown in `InlineLayout`. */
  VIEW_CR_TITLE: 'Podgląd zgłoszenia kontaktowego',

  /** Label for the header button that opens the status-update modal. */
  VIEW_CR_BUTTON_UPDATE_STATUS: 'Zmiana statusu',

  /**
   * Label for the header button that opens the "Complete & send response" modal
   * (RR-1097). Only the assigned admin sees it, and only while the request is
   * `in_progress`.
   */
  VIEW_CR_BUTTON_COMPLETE: 'Zakończ i wyślij odpowiedź',

  /** Label for the header button that opens the assign modal (request is unassigned). */
  VIEW_CR_BUTTON_ASSIGN: 'Assign',

  /** Label for the header button that opens the assign modal (request already has an assignee). */
  VIEW_CR_BUTTON_REASSIGN: 'Przydziel ponownie',

  /** Full-page error message shown when the API call fails or returns no data. */
  VIEW_CR_ERROR_LOAD: 'Nie udało się wczytać zgłoszenia. Spróbuj ponownie.',

  /** Success snackbar message shown after a status transition completes. */
  VIEW_CR_SUCCESS_STATUS_UPDATED: 'Status został pomyślnie zaktualizowany.',

  // "Complete & send response" modal (RR-1097)

  /** Modal title. */
  COMPLETE_CR_TITLE: 'Zakończ i wyślij odpowiedź',

  /** Read-only "current status" field label inside the modal. */
  COMPLETE_CR_FIELD_CURRENT_STATUS: 'Aktualny status',

  /** Read-only "new status" field label (fixed to "Zakończone"). */
  COMPLETE_CR_FIELD_NEW_STATUS: 'Nowy Status',

  /** Bold lead-in of the irreversible-action banner. */
  COMPLETE_CR_BANNER_BOLD: 'Zmiana statusu jest nieodwracalna. ',

  /** Remainder of the irreversible-action banner. */
  COMPLETE_CR_BANNER_REST:
    'Po zaktualizowaniu nie będzie można przywrócić poprzedniego statusu.',

  /** Heading above the file upload area. */
  COMPLETE_CR_SECTION_UPLOAD: 'Prześlij plik',

  /** Label + placeholder for the response note textarea. */
  COMPLETE_CR_FIELD_NOTES: 'Response Notes',

  /** Cancel button. */
  COMPLETE_CR_BUTTON_CANCEL: 'Anuluj',

  /** Submit button (idle). */
  COMPLETE_CR_BUTTON_SUBMIT: 'Wyślij i oznacz jako zakończone',

  /** Submit button (in-flight). */
  COMPLETE_CR_BUTTON_SUBMITTING: 'Wysyłanie…',

  /** Success snackbar shown after a successful completion. */
  COMPLETE_CR_SUCCESS: 'Zgłoszenie kontaktowe zostało pomyślnie zakończone.',

  /** Notes-length validation error (max 500 chars). */
  COMPLETE_CR_ERROR_NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',

  /** Toast shown when submitting with neither a note nor an attachment. */
  COMPLETE_CR_ERROR_NOTES_OR_ATTACHMENT:
    'Response Notes or at least one attachment is required to complete this Contact Request.',

  /** Toast shown when one or more file uploads fail. */
  COMPLETE_CR_ERROR_UPLOAD_FAILED:
    'Nie udało się przesłać niektórych plików. Spróbuj ponownie.',

  // Navigation tabs

  /** Label for the "basic information" navigation tab. */
  VIEW_CR_NAV_BASIC_INFO: 'Podstawowe informacje',

  /** Label for the "admin notes" navigation tab. */
  VIEW_CR_NAV_ADMIN_NOTES: 'Notatki administratora',

  /** Label for the "assignee details" navigation tab. */
  VIEW_CR_NAV_ASSIGNEE_DETAILS: 'Assignee Details',

  /**
   * Label for the "summary" navigation tab on the my-assigned-task view
   * (RR-1096). The my-tasks view names this tab differently from the default
   * view's "Podstawowe informacje".
   */
  VIEW_CR_NAV_SUMMARY: 'Podsumowanie zgłoszenia',

  /** Label for the completion "response" navigation tab (RR-1096, English per design). */
  VIEW_CR_NAV_RESPONSE: 'Response',

  /** Empty-state message shown in the Response tab when no response exists yet. */
  VIEW_CR_RESPONSE_EMPTY_STATE: 'No response has been provided yet.',

  /** Label for the status-history navigation tab (RR-1096). */
  VIEW_CR_NAV_HISTORY: 'Historia',

  // Section headings

  /** Heading for the client details card. */
  VIEW_CR_SECTION_BASIC_DATA: 'Podstawowe dane',

  /**
   * Heading for the contact-request details card on the my-assigned-task view
   * (RR-1096). Design names this section "Szczegóły zgłoszenia" instead of the
   * default view's "Podstawowe dane".
   */
  VIEW_CR_SECTION_DETAILS: 'Szczegóły zgłoszenia',

  /** Heading for the client address card. */
  VIEW_CR_SECTION_ADDRESS: 'Dane adresowe',

  /** Heading for the admin notes card. */
  VIEW_CR_SECTION_ADMIN_NOTES: 'Notatki administratora',

  // Field labels — basic info

  /** Field label for the sequential client identifier (e.g. CLIENT000001). */
  VIEW_CR_FIELD_CLIENT_ID: 'ID klienta',

  /** Field label for the client's display name. */
  VIEW_CR_FIELD_CLIENT_NAME: 'Nazwa klienta',

  /** Field label for the client's email address. */
  VIEW_CR_FIELD_EMAIL: 'Adres e-mail',

  /** Field label for the client's phone number. */
  VIEW_CR_FIELD_PHONE: 'Numer telefonu',

  /** Field label for the ISO-8601 creation timestamp, formatted for display. */
  VIEW_CR_FIELD_CREATED_AT: 'Data utworzenia',

  /** Field label for the contact request subject line. */
  VIEW_CR_FIELD_SUBJECT: 'Temat',

  /** Field label for the contact request message body. */
  VIEW_CR_FIELD_MESSAGE: 'Wiadomość',

  // Field labels — address

  /** Field label for the first line of the client's postal address. */
  VIEW_CR_FIELD_ADDRESS_LINE1: 'Adres (linia 1)',

  /** Field label for the optional second line of the client's postal address. */
  VIEW_CR_FIELD_ADDRESS_LINE2: 'Adres (linia 2)',

  /** Field label for the client's city. */
  VIEW_CR_FIELD_CITY: 'Miejscowość',

  /** Field label for the client's province / voivodeship. */
  VIEW_CR_FIELD_STATE: 'Województwo',

  /** Field label for the client's postal code. */
  VIEW_CR_FIELD_ZIP: 'Kod pocztowy',

  /** Field label for the client's country. */
  VIEW_CR_FIELD_COUNTRY: 'Kraj',

  // Admin notes tab

  /** Empty-state message shown when the request has no admin notes yet. */
  VIEW_CR_NOTES_EMPTY: 'Administrator nie dodał jeszcze żadnych notatek.',

  // Assignee details tab

  /** Heading for the assignee details card. */
  VIEW_CR_SECTION_ASSIGNEE_DATA: 'Assignee Details',

  /** Field label for the assigned admin's sequential id. */
  VIEW_CR_FIELD_ASSIGNEE_ID: 'ID',

  /** Field label for the assigned admin's name. */
  VIEW_CR_FIELD_ASSIGNEE_NAME: 'Imię i nazwisko administratora',

  /** Field label for the assigned admin's email address. */
  VIEW_CR_FIELD_ASSIGNEE_EMAIL: 'Adres e-mail',

  /** Field label for the assigned admin's contact number (RR-1096). */
  VIEW_CR_FIELD_ASSIGNEE_PHONE: 'Numer telefonu',

  /** Field label for the assigned admin's role (RR-1096). */
  VIEW_CR_FIELD_ASSIGNEE_ROLE: 'Rola',

  /** Empty-state message shown when the request has not been assigned yet. */
  VIEW_CR_ASSIGNEE_EMPTY: 'This request has not been assigned to any user yet.',

  /** Success snackbar message shown after a successful assignment. */
  VIEW_CR_SUCCESS_ASSIGNED: 'Zgłoszenie kontaktowe zostało przypisane.',

  // Assigned-by details (RR-1096)

  /** Heading for the "assigned by" details card. */
  VIEW_CR_SECTION_ASSIGNED_BY: 'Dane osoby przydzielającej',

  /** Field label for the name of the admin who performed the assignment. */
  VIEW_CR_FIELD_ASSIGNED_BY_NAME: 'Imię i nazwisko',

  /** Field label for the email of the admin who performed the assignment. */
  VIEW_CR_FIELD_ASSIGNED_BY_EMAIL: 'Adres e-mail',

  /** Field label for the role of the admin who performed the assignment. */
  VIEW_CR_FIELD_ASSIGNED_BY_ROLE: 'Rola',

  // Role display labels (RR-1096 — used in assignee/assigned-by/history)

  /** Display label for the CLIENT role. */
  VIEW_CR_ROLE_CLIENT: 'Klient',

  /** Display label for the ADMIN role. */
  VIEW_CR_ROLE_ADMIN: 'Administrator',

  /** Display label for the SUPER_ADMIN role. */
  VIEW_CR_ROLE_SUPER_ADMIN: 'Super administrator',

  /** Display label for the TECHNICIAN role. */
  VIEW_CR_ROLE_TECHNICIAN: 'Technik',

  // Response tab (RR-1096)

  /** Sub-heading for the completion attachments list inside the Response tab. */
  VIEW_CR_RESPONSE_ATTACHMENTS: 'Attachments',

  /** Sub-heading for the completion note inside the Response tab. */
  VIEW_CR_RESPONSE_NOTES: 'Response Notes',

  /** Placeholder shown when a response field has no value. */
  VIEW_CR_RESPONSE_EMPTY: '—',

  // History tab (RR-1096)

  /** Event label for the initial "created" history entry. */
  VIEW_CR_HISTORY_CREATED: 'Utworzono zgłoszenie kontaktowe',

  /** Event label for a status-change history entry. */
  VIEW_CR_HISTORY_STATUS_CHANGE: 'Zmiana statusu',

  /** Prefix for the actor line on the "created" history entry. */
  VIEW_CR_HISTORY_CREATED_BY: 'Utworzone przez',

  /** Prefix for the actor line on a status-change history entry. */
  VIEW_CR_HISTORY_UPDATED_BY: 'Zaktualizowane przez',

  /** Title shown when the request has no history entries. */
  VIEW_CR_HISTORY_EMPTY_TITLE: 'Brak historii',

  /** Description shown when the request has no history entries. */
  VIEW_CR_HISTORY_EMPTY_DESCRIPTION:
    'Dla tego zgłoszenia nie zarejestrowano jeszcze żadnych zdarzeń.',

  // ─── UpdateStatusModal ────────────────────────────────────────────────────

  /** Dialog title rendered at the top of the status-update modal. */
  UPDATE_STATUS_MODAL_TITLE: 'Zmiana statusu zgłoszenia kontaktowego',

  /** Label for the read-only "current status" field. */
  UPDATE_STATUS_FIELD_CURRENT: 'Aktualny status',

  /** Label / floating placeholder for the "new status" dropdown. */
  UPDATE_STATUS_FIELD_NEW: 'Nowy status',

  /** Label for the optional admin note textarea. */
  UPDATE_STATUS_FIELD_NOTE: 'Notatki administratora',

  /** Parenthetical suffix appended to the note label to mark it optional. */
  UPDATE_STATUS_FIELD_NOTE_OPTIONAL: '(Opcjonalne)',

  /** Placeholder text inside the note textarea. */
  UPDATE_STATUS_PLACEHOLDER_NOTE: 'Notatka administratora',

  /** Bold portion of the irreversibility warning banner. */
  UPDATE_STATUS_BANNER_BOLD: 'Zmiana statusu jest nieodwracalna.',

  /** Non-bold continuation of the irreversibility warning banner. */
  UPDATE_STATUS_BANNER_REST:
    ' Po zaktualizowaniu nie będzie można przywrócić poprzedniego statusu.',

  /** Disabled-item label shown in the dropdown when no transitions are available. */
  UPDATE_STATUS_EMPTY_OPTIONS: 'Brak dostępnych statusów',

  /** Label for the cancel button. */
  UPDATE_STATUS_BUTTON_CANCEL: 'Anuluj',

  /** Label for the submit button in its idle state. */
  UPDATE_STATUS_BUTTON_SUBMIT: 'Zmiana statusu',

  /** Label for the submit button while the API request is in-flight. */
  UPDATE_STATUS_BUTTON_SUBMITTING: 'Aktualizowanie statusu…',

  /**
   * Label for the submit button in step 1 of the cancellation flow.
   * Replaces the normal submit label when "Cancelled" is selected.
   */
  UPDATE_STATUS_BUTTON_CONTINUE: 'Continue',

  // ─── CancelContactRequestModal ───────────────────────────────────────────

  /** Dialog title for the cancellation confirmation modal. */
  CANCEL_CR_MODAL_TITLE: 'Confirm Cancellation',

  /** Subtitle / description shown below the title. */
  CANCEL_CR_MODAL_DESC:
    'Proszę podać powód anulowania. Informacja zostanie przesłana do klienta e-mailem.',

  /** Heading for the read-only client-details section. */
  CANCEL_CR_SECTION_CLIENT_DATA: 'Dane klienta',

  /** Field label for the client name row in the client-details section. */
  CANCEL_CR_FIELD_CLIENT_NAME: 'Nazwa klienta',

  /** Field label for the client email row in the client-details section. */
  CANCEL_CR_FIELD_CLIENT_EMAIL: 'Adres e-mail',

  /** Floating label for the cancellation reason textarea. */
  CANCEL_CR_REASON_LABEL: 'Enter a Reason for Cancellation',

  /** Placeholder text inside the cancellation reason textarea. */
  CANCEL_CR_REASON_PLACEHOLDER: 'Wpisz powód anulowania…',

  /** Inline validation error when the reason is left blank. */
  CANCEL_CR_REASON_ERROR: 'To pole jest wymagane',

  /** Label for the "go back" button in the cancellation confirmation modal. */
  CANCEL_CR_BUTTON_BACK: 'Wstecz',

  /** Label for the confirm-cancel submit button in its idle state. */
  CANCEL_CR_BUTTON_SUBMIT: 'Cancel Request',

  /** Label for the confirm-cancel submit button while the API request is in-flight. */
  CANCEL_CR_BUTTON_SUBMITTING: 'Cancelling Request...',

  // ─── ViewContactRequest — cancellation-specific snackbar ─────────────────

  /** Success snackbar message shown after a contact request is cancelled. */
  VIEW_CR_SUCCESS_CANCELLED: 'Contact request has been cancelled successfully.',

  // ─── AssignContactRequestModal ────────────────────────────────────────────

  /** Dialog title rendered at the top of the assign modal. */
  ASSIGN_CR_MODAL_TITLE: 'Assign Contact Request',

  /** Section heading above the admin picker. */
  ASSIGN_CR_SECTION_TITLE: 'Dane osoby przydzielonej',

  /** Floating label for the admin-name searchable dropdown. */
  ASSIGN_CR_FIELD_ADMIN_NAME: 'Imię i nazwisko administratora',

  /** Label for the read-only admin id field, auto-filled from the selection. */
  ASSIGN_CR_FIELD_ADMIN_ID: 'Admin ID',

  /** Placeholder text inside the admin search input. */
  ASSIGN_CR_SEARCH_PLACEHOLDER: 'Search',

  /** Shown while the eligible-assignees list is loading. */
  ASSIGN_CR_LOADING: 'Loading…',

  /** Shown in the dropdown when a search yields no matching admins. */
  ASSIGN_CR_NO_RESULTS: 'No matching admins.',

  /** Shown below the field when there are no eligible admins at all. */
  ASSIGN_CR_NO_ADMINS: 'No admins available.',

  /** Link label that navigates to the create-admin page. */
  ASSIGN_CR_ADD_ADMIN_LINK: 'Dodaj administratora',

  /** Label for the cancel button. */
  ASSIGN_CR_BUTTON_CANCEL: 'Anuluj',

  /** Label for the submit button in its idle state. */
  ASSIGN_CR_BUTTON_SUBMIT: 'Przydziel',

  /** Label for the submit button while the API request is in-flight. */
  ASSIGN_CR_BUTTON_SUBMITTING: 'Assigning…',

  // Reassign flow (two-step: pick → confirm)

  /** Dialog title when the request already has an assignee (reassignment). */
  ASSIGN_CR_REASSIGN_MODAL_TITLE: 'Ponownie przydziel zgłoszenie kontaktowe',

  /** Heading of the current-assignment card on the reassign select step. */
  ASSIGN_CR_REASSIGN_SECTION_CURRENT: 'Szczegóły przypisania',

  /** Assigned-user field label inside the reassign select-step card. */
  ASSIGN_CR_REASSIGN_FIELD_ASSIGNED_USER: 'Przydzielony użytkownik',

  /** Client-name field label inside the reassign select-step card. */
  ASSIGN_CR_REASSIGN_FIELD_CLIENT_NAME: 'Nazwa klienta',

  /** Section heading above the new-assignee picker on the reassign select step. */
  ASSIGN_CR_REASSIGN_SECTION_NEW: 'Dane nowej osoby przydzielonej',

  /** Floating label for the new-assignee dropdown on the reassign select step. */
  ASSIGN_CR_REASSIGN_FIELD_ADMIN_NAME: 'Imię i nazwisko administratora',

  /** Heading of the confirmation step showing the before/after assignment. */
  ASSIGN_CR_CONFIRM_TITLE: 'Potwierdź ponowne przydzielenie',

  /** Sub-heading explaining the confirmation step. */
  ASSIGN_CR_CONFIRM_SUBTITLE:
    'Przejrzyj zmiany w przydzieleniu przed potwierdzeniem. Odpowiedni użytkownicy zostaną powiadomieni.',

  /** Heading of the "before" card showing the current assignment. */
  ASSIGN_CR_CONFIRM_SECTION_CURRENT: 'Szczegóły przypisania',

  /** Heading of the "after" card showing the updated assignment. */
  ASSIGN_CR_CONFIRM_SECTION_UPDATED: 'Zaktualizowane szczegóły przypisania',

  /** Field label for the assignee shown inside the confirmation cards. */
  ASSIGN_CR_FIELD_ASSIGNED_USER: 'Przydzielony użytkownik',

  /** Field label for the client shown inside the confirmation cards. */
  ASSIGN_CR_FIELD_CLIENT_NAME: 'Nazwa klienta',

  /** Checkbox label opting into notifying the client & admin by email. */
  ASSIGN_CR_NOTIFY_LABEL:
    'Powiadom klienta i administratora, że zostanie do nich wysłana wiadomość e-mail zawierająca odpowiednie szczegóły.',

  /** Label for the "go back to picker" button in the confirmation step. */
  ASSIGN_CR_BUTTON_BACK: 'Wstecz',

  /** Label for the button advancing the picker step to the confirmation step. */
  ASSIGN_CR_BUTTON_CONTINUE: 'Przejrzyj zmiany',

  /** Label for the final confirm button in its idle state. */
  ASSIGN_CR_BUTTON_CONFIRM: 'Przydziel ponownie',

  /** Label for the final confirm button while the API request is in-flight. */
  ASSIGN_CR_BUTTON_CONFIRMING: 'Ponowne przydzielanie…',
} as const;

export default pl;
